import Statement from './Statement';
import Formula from './formula/Formula';
import { Vector, Option } from 'prelude-ts';

/**
 * An action specifies a modification to a subgoal.
 */
export abstract class Action {
  /**
   * Apply the action to a statement. This gives back zero or more
   * statements, which are meant to replace the input statement.
   * Returning no statements corresponds to deleting the original
   * statement.
   *
   * Returned statements might already be solved (i.e. resources and
   * requirements might be empty), which need to be handled at a higher
   * level.
   *
   * @param to The statement to apply the action to.
   */
  abstract apply(to: Statement): Vector<Statement>;

  /**
   * Apply this action followed by the given one.
   */
  andThen(next: Action): Action {
    return new AndThen(this, next);
  }

  /**
   *  Lookup a formula by its unique id in a `Vector`.
   */
  protected static findById(
    id: Formula['id'],
    formulas: Vector<Formula>
  ): Option<Formula> {
    return formulas.find(f => f.id === id);
  }
}

/**
 * Remove a matching resource-requirement pair.
 */
export class Match extends Action {
  private readonly resource: Formula['id'];
  private readonly requirement: Formula['id'];

  constructor(resource: Formula['id'], requirement: Formula['id']) {
    super();
    this.resource = resource;
    this.requirement = requirement;
  }

  apply(to: Statement): Vector<Statement> {
    const newResources = to.resources.filter(f => f.id !== this.resource);
    const newRequirements = to.requirements.filter(
      f => f.id !== this.requirement
    );
    return Vector.of(to.update(newResources, newRequirements));
  }
}

/**
 * Negate a formula and move it from resources to requirements
 * or vice versa.
 */
export class Negate extends Action {
  private readonly formula: Formula['id'];

  constructor(formula: Formula['id']) {
    super();
    this.formula = formula;
  }

  apply(to: Statement): Vector<Statement> {
    let newResources, newRequirements: Vector<Formula>;
    try {
      // Try to move from resources to requirements.
      [newResources, newRequirements] = Negate.move(
        this.formula,
        to.resources,
        to.requirements
      );
    } catch {
      try {
        // Try the other way.
        [newRequirements, newResources] = Negate.move(
          this.formula,
          to.requirements,
          to.resources
        );
      } catch {
        // It isn't in either; ignore action.
        return Vector.of(to);
      }
    }
    return Vector.of(to.update(newResources, newRequirements));
  }

  /**
   * Move a formula from `left` to `right` while also negating it.
   *
   * Raises an exception if the formula is not in `left`.
   */
  private static move(
    id: Formula['id'],
    left: Vector<Formula>,
    right: Vector<Formula>
  ): [Vector<Formula>, Vector<Formula>] {
    const formula = Negate.findById(id, left);

    if (formula.isSome()) {
      const newLeft = left.removeFirst(f => f.id === id);
      const newRight = right.append(formula.get().negated());
      return [newLeft, newRight];
    } else {
      throw Error();
    }
  }
}

/**
 * Mark a subgoal as solved.
 */
export class Solve extends Action {
  apply(_to: Statement): Vector<Statement> {
    return Vector.empty();
  }
}

/**
 * Replace a formula with new formulae.
 */
export class Replace extends Action {
  private readonly remove: Formula['id'];
  private readonly add: Vector<Formula>;
  private readonly preserveIds: boolean;

  constructor(
    remove: Formula['id'],
    add: Vector<Formula>,
    preserveIds: boolean = true
  ) {
    super();
    this.remove = remove;
    this.add = add;
    this.preserveIds = preserveIds;
  }

  apply(to: Statement): Vector<Statement> {
    const replace = (s: Vector<Formula>) =>
      s.flatMap(f => (f.id === this.remove ? this.add : Vector.of(f)));

    return Vector.of(
      this.preserveIds
        ? to.update(replace(to.resources), replace(to.requirements))
        : new Statement(replace(to.resources), replace(to.requirements))
    );
  }
}

/**
 * Remove the given formula.
 */
export class Remove extends Replace {
  constructor(formula: Formula['id'], preserveIds: boolean = true) {
    super(formula, Vector.empty(), preserveIds);
  }
}

/**
 * Add the given formula to resources.
 */
export class AddResource extends Action {
  private readonly resource: Formula;

  constructor(resource: Formula) {
    super();
    this.resource = resource;
  }

  apply(to: Statement): Vector<Statement> {
    const newResources = to.resources.append(this.resource);
    return Vector.of(to.update(newResources, to.requirements));
  }
}

/**
 * Replace the given formula with a new formula from the given
 * list. A new subgoal is created for each formula in the replacement
 * list.
 */
export class ReplaceSplit extends Action {
  private readonly remove: Formula['id'];
  private readonly replacements: Vector<Formula>;

  constructor(remove: Formula['id'], replacements: Vector<Formula>) {
    super();
    this.remove = remove;
    this.replacements = replacements;
  }

  apply(to: Statement): Vector<Statement> {
    if (to.hasFormula(this.remove)) {
      return this.replacements.flatMap(newFormula =>
        new Replace(this.remove, Vector.of(newFormula), false).apply(to)
      );
    } else {
      return Vector.of(to);
    }
  }
}

export type FormulaFilter = (formula: Formula['id']) => boolean;

/**
 * Similar to `ReplaceSplit`, but removes some of the formulae
 * after replacing according to provided criteria. Each new
 * subformula gets to keep different formulae.
 */
export class ReplaceSplitFilter extends Action {
  private readonly remove: Formula['id'];
  private readonly replacements: Vector<[Formula, FormulaFilter]>;

  constructor(
    remove: Formula['id'],
    replacements: Vector<[Formula, FormulaFilter]>
  ) {
    super();
    this.remove = remove;
    this.replacements = replacements;
  }

  apply(to: Statement): Vector<Statement> {
    if (to.hasFormula(this.remove)) {
      return this.replacements.flatMap(([newFormula, keep]) =>
        new Replace(this.remove, Vector.of(newFormula), false)
          .andThen(new Filter(f => f === newFormula.id || keep(f)))
          .apply(to)
      );
    } else {
      return Vector.of(to);
    }
  }
}

/**
 * Apply the first action followed by the second.
 */
class AndThen extends Action {
  private readonly first: Action;
  private readonly second: Action;

  constructor(first: Action, second: Action) {
    super();
    this.first = first;
    this.second = second;
  }

  apply(to: Statement): Vector<Statement> {
    return this.first.apply(to).flatMap(s => this.second.apply(s));
  }
}

/**
 * Apply an action if the given condition holds.
 */
export class When extends Action {
  private readonly condition: (statement: Statement) => boolean;
  private readonly action: Action;

  constructor(condition: (statement: Statement) => boolean, action: Action) {
    super();
    this.condition = condition;
    this.action = action;
  }

  apply(to: Statement): Vector<Statement> {
    if (this.condition(to)) {
      return this.action.apply(to);
    } else {
      return Vector.of(to);
    }
  }
}

/**
 * Remove formulae that do not satisfy the given condition.
 */
export class Filter extends Action {
  private readonly keep: FormulaFilter;

  constructor(keep: FormulaFilter) {
    super();
    this.keep = keep;
  }

  apply(to: Statement): Vector<Statement> {
    return Vector.of(
      new Statement(
        to.resources.filter(f => this.keep(f.id)),
        to.requirements.filter(f => this.keep(f.id))
      )
    );
  }
}
